import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { apiRequest } from '../api/client';
import { useSession } from '../api/session';
import { useMutation } from '../api/useApi';
import { useErrorMessage } from '../errors';
import { useLocale } from '../locale';

/** The password policy's minimum (ADR-0023). */
const MIN_LENGTH = 12;

/**
 * The only screen an account with an initial password can reach (ADR-0049). The server refuses
 * everything else until the password is changed; this screen is what that looks like to the person.
 * Changing it ends every session, so they then sign in with the password only they know.
 */
export function ChangeInitialPasswordPage() {
  const { t } = useLocale();
  const { signOut } = useSession();
  const errorMessage = useErrorMessage();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const change = useMutation(() =>
    apiRequest('/api/v1/me/password', {
      method: 'POST',
      body: { currentPassword, newPassword },
    }),
  );
  const mismatch = confirmation.length > 0 && confirmation !== newPassword;
  const valid =
    currentPassword.length > 0 && newPassword.length >= MIN_LENGTH && newPassword === confirmation;
  return (
    <Box
      component="main"
      sx={{ minBlockSize: '100vh', display: 'grid', placeItems: 'center', padding: 2 }}
    >
      <Paper variant="outlined" sx={{ padding: 3, inlineSize: '100%', maxInlineSize: 460 }}>
        <Stack
          component="form"
          spacing={2}
          onSubmit={(event) => {
            event.preventDefault();
            if (valid) {
              void change.run(undefined).then(
                () => signOut(),
                () => undefined,
              );
            }
          }}
        >
          <Typography component="h1" variant="h5">
            {t('salesWorkspace.password.title')}
          </Typography>
          <Alert severity="info" variant="outlined">
            {t('salesWorkspace.password.hint')}
          </Alert>
          <TextField
            required
            type="password"
            label={t('salesWorkspace.password.current')}
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            autoComplete="current-password"
            slotProps={{ htmlInput: { dir: 'ltr', maxLength: 200 } }}
          />
          <TextField
            required
            type="password"
            label={t('salesWorkspace.password.new')}
            helperText={t('salesWorkspace.team.initialPasswordHint')}
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            autoComplete="new-password"
            slotProps={{ htmlInput: { dir: 'ltr', maxLength: 200 } }}
          />
          <TextField
            required
            type="password"
            label={t('setPassword.confirm')}
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            autoComplete="new-password"
            error={mismatch}
            helperText={mismatch ? t('setPassword.mismatch') : ' '}
            slotProps={{ htmlInput: { dir: 'ltr', maxLength: 200 } }}
          />
          {change.error ? (
            <Alert severity="error" variant="outlined" role="alert">
              {errorMessage(change.error)}
            </Alert>
          ) : null}
          <Button type="submit" variant="contained" disabled={!valid || change.pending}>
            {t('salesWorkspace.password.save')}
          </Button>
          <Button variant="text" onClick={() => void signOut()}>
            {t('salesWorkspace.password.signOut')}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
